import { useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import * as THREE from 'three';

export interface AvatarViewerRef {
  /** Decodes base64 MP3 audio, plays it through the Web Audio analyser,
   *  and drives the FFT-based lip-sync. Returns a Promise that resolves
   *  when playback ends. */
  speakFromBase64(base64: string): Promise<void>;
}

// ─── Internal audio state (lives outside React render cycle) ───────────────

interface AudioRefs {
  ctx: AudioContext | null;
  analyser: AnalyserNode | null;
  freqData: Uint8Array<ArrayBuffer> | null;
}

// ─── Component ─────────────────────────────────────────────────────────────

const AvatarViewer = forwardRef<AvatarViewerRef>((_, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isSpeakingRef = useRef(false);
  const audioRefs = useRef<AudioRefs>({ ctx: null, analyser: null, freqData: null });
  const animIdRef = useRef(0);

  // ── Imperative API exposed to App.tsx ──────────────────────────────────
  useImperativeHandle(ref, () => ({
    speakFromBase64: async (base64: string): Promise<void> => {
      // Lazily create AudioContext (requires prior user gesture to run)
      if (!audioRefs.current.ctx) {
        audioRefs.current.ctx = new AudioContext();
      }
      const ctx = audioRefs.current.ctx;
      if (ctx.state === 'suspended') await ctx.resume();

      // Decode base64 → ArrayBuffer
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

      let audioBuffer: AudioBuffer;
      try {
        audioBuffer = await ctx.decodeAudioData(bytes.buffer.slice(0));
      } catch {
        isSpeakingRef.current = false;
        return; // gracefully skip if audio is invalid (e.g. empty TTS response)
      }

      // Create AnalyserNode once
      if (!audioRefs.current.analyser) {
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.75;
        analyser.connect(ctx.destination);
        audioRefs.current.analyser = analyser;
        audioRefs.current.freqData = new Uint8Array(analyser.frequencyBinCount) as unknown as Uint8Array<ArrayBuffer>;
      }

      return new Promise<void>((resolve) => {
        const source = ctx.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(audioRefs.current.analyser!);
        isSpeakingRef.current = true;
        source.start();
        source.onended = () => {
          isSpeakingRef.current = false;
          resolve();
        };
      });
    },
  }));

  // ── Three.js scene lifecycle ───────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;

    // Scene
    const scene = new THREE.Scene();

    // Camera
    const camera = new THREE.PerspectiveCamera(
      50,
      container.clientWidth / container.clientHeight,
      0.1,
      1000,
    );
    camera.position.z = 6;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    container.appendChild(renderer.domElement);

    // ── Lights ─────────────────────────────────────────────────────────
    scene.add(new THREE.AmbientLight(0x222222));

    const cyanLight = new THREE.PointLight(0x00ffcc, 1.5, 20);
    cyanLight.position.set(2, 2, 3);
    scene.add(cyanLight);

    const purpleLight = new THREE.PointLight(0x8a2be2, 1.2, 20);
    purpleLight.position.set(-2, -2, 2);
    scene.add(purpleLight);

    // ── RAMMUX group ───────────────────────────────────────────────────
    const rammuxGroup = new THREE.Group();
    scene.add(rammuxGroup);

    // Skull (flat-shaded icosahedron → angular cyberpunk look)
    const skullGeo = new THREE.IcosahedronGeometry(1.5, 2);
    const skullMat = new THREE.MeshStandardMaterial({
      color: 0x111111,
      metalness: 0.9,
      roughness: 0.25,
      flatShading: true,
    });
    rammuxGroup.add(new THREE.Mesh(skullGeo, skullMat));

    // Holographic wireframe shell
    const wireGeo = new THREE.IcosahedronGeometry(1.63, 2);
    const wireMat = new THREE.MeshBasicMaterial({
      color: 0x00ffcc,
      wireframe: true,
      transparent: true,
      opacity: 0.1,
    });
    const wireShell = new THREE.Mesh(wireGeo, wireMat);
    rammuxGroup.add(wireShell);

    // Eyes — cybernetic optics
    const eyeGeo = new THREE.SphereGeometry(0.22, 16, 16);
    const leftEye = new THREE.Mesh(eyeGeo, new THREE.MeshBasicMaterial({ color: 0x00ffcc }));
    leftEye.position.set(-0.6, 0.4, 1.35);
    rammuxGroup.add(leftEye);

    const rightEye = new THREE.Mesh(eyeGeo, new THREE.MeshBasicMaterial({ color: 0x00ffcc }));
    rightEye.position.set(0.6, 0.4, 1.35);
    rammuxGroup.add(rightEye);

    // Jaw bars — FFT lip-sync visualizer
    const jawGroup = new THREE.Group();
    jawGroup.position.set(0, -0.88, 1.26);
    rammuxGroup.add(jawGroup);

    const JAW_BARS = 7;
    const jawBars: THREE.Mesh<THREE.BoxGeometry, THREE.MeshStandardMaterial>[] = [];
    for (let i = 0; i < JAW_BARS; i++) {
      const geo = new THREE.BoxGeometry(0.12, 0.55, 0.1);
      const mat = new THREE.MeshStandardMaterial({
        color: 0x333333,
        metalness: 1.0,
        roughness: 0.2,
      });
      const bar = new THREE.Mesh(geo, mat);
      bar.position.x = (i - Math.floor(JAW_BARS / 2)) * 0.2;
      jawGroup.add(bar);
      jawBars.push(bar);
    }

    // Ambient particles (cyberspace background)
    const particlesGeo = new THREE.BufferGeometry();
    const posArr = new Float32Array(200 * 3);
    for (let i = 0; i < posArr.length; i++) posArr[i] = (Math.random() - 0.5) * 15;
    particlesGeo.setAttribute('position', new THREE.BufferAttribute(posArr, 3));
    const particles = new THREE.Points(
      particlesGeo,
      new THREE.PointsMaterial({ size: 0.05, color: 0x00ffcc, transparent: true, opacity: 0.5 }),
    );
    scene.add(particles);

    // Reusable Color objects to avoid GC pressure in the animation loop
    const cyanColor = new THREE.Color(0x00ffcc);
    const darkColor = new THREE.Color(0x333333);

    // ── Mouse tracking (RAMMUX follows cursor) ─────────────────────
    let mouseX = 0;
    let mouseY = 0;
    const onMouseMove = (e: MouseEvent) => {
      mouseX = (e.clientX / window.innerWidth) * 2 - 1;
      mouseY = -(e.clientY / window.innerHeight) * 2 + 1;
    };
    window.addEventListener('mousemove', onMouseMove);

    // ── Animation loop ──────────────────────────────────────────────
    let time = 0;
    const animate = () => {
      animIdRef.current = requestAnimationFrame(animate);
      time += 0.05;

      // Smooth head rotation toward cursor
      rammuxGroup.rotation.x = THREE.MathUtils.lerp(rammuxGroup.rotation.x, mouseY * 0.3, 0.05);
      rammuxGroup.rotation.y = THREE.MathUtils.lerp(rammuxGroup.rotation.y, mouseX * 0.5, 0.05);

      // Subtle floating
      rammuxGroup.position.y = Math.sin(time * 0.5) * 0.18;
      wireShell.rotation.y -= 0.0018;
      particles.rotation.y += 0.0008;

      // ── FFT lip-sync ────────────────────────────────────────────
      const { analyser, freqData } = audioRefs.current;
      if (isSpeakingRef.current && analyser && freqData) {
        analyser.getByteFrequencyData(freqData);

        // Voice fundamentals live in bins 2–16 (≈344–2756 Hz @ 44100Hz, 256 FFT)
        // Map each bar to a different voice-range frequency bin
        jawBars.forEach((bar, i) => {
          const binIndex = 2 + i * 2;
          const normalized = (freqData[binIndex] ?? 0) / 255; // 0..1
          const targetScale = 1 + normalized * 2.8;
          bar.scale.y = THREE.MathUtils.lerp(bar.scale.y, targetScale, 0.35);
          bar.material.color.lerpColors(darkColor, cyanColor, normalized);
        });

        // Eyes pulse with average vocal amplitude
        const avgAmp =
          Array.from(freqData.slice(2, 16)).reduce((a, b) => a + b, 0) / (14 * 255);
        const eyeScale = 1 + avgAmp * 0.6;
        leftEye.scale.setScalar(THREE.MathUtils.lerp(leftEye.scale.x, eyeScale, 0.2));
        rightEye.scale.setScalar(THREE.MathUtils.lerp(rightEye.scale.x, eyeScale, 0.2));
        cyanLight.intensity = THREE.MathUtils.lerp(cyanLight.intensity, 1.5 + avgAmp * 4, 0.15);
      } else {
        // ── Idle: smooth return to rest ─────────────────────────
        jawBars.forEach((bar) => {
          bar.scale.y = THREE.MathUtils.lerp(bar.scale.y, 1, 0.07);
          bar.material.color.lerp(darkColor, 0.07);
        });
        leftEye.scale.setScalar(THREE.MathUtils.lerp(leftEye.scale.x, 1, 0.07));
        rightEye.scale.setScalar(THREE.MathUtils.lerp(rightEye.scale.x, 1, 0.07));
        cyanLight.intensity = THREE.MathUtils.lerp(cyanLight.intensity, 1.5, 0.04);
      }

      renderer.render(scene, camera);
    };
    animate();

    // ── Resize ──────────────────────────────────────────────────────
    const onResize = () => {
      camera.aspect = container.clientWidth / container.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(container.clientWidth, container.clientHeight);
    };
    window.addEventListener('resize', onResize);

    // ── AudioContext init on first user interaction ──────────────────
    // Browsers suspend AudioContext until a user gesture. Resume early.
    const initAudio = () => {
      if (audioRefs.current.ctx?.state === 'suspended') {
        void audioRefs.current.ctx.resume();
      }
    };
    window.addEventListener('click', initAudio);
    window.addEventListener('keydown', initAudio);

    return () => {
      cancelAnimationFrame(animIdRef.current);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('click', initAudio);
      window.removeEventListener('keydown', initAudio);
      renderer.dispose();
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
    };
  }, []); // mount once

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        background: 'radial-gradient(circle at 50% 40%, #12122a 0%, #000000 100%)',
      }}
    />
  );
});

AvatarViewer.displayName = 'AvatarViewer';
export default AvatarViewer;
